/**
 * @file Device detail (`#/devices/:id`) — admin only.
 *
 * Single-device view. For Flexi-mirrored devices, most fields are read-only
 * (must be fixed in Abra, never in Gear). Gear-owned devices are
 * fully editable. Notes, status, and currentHolder can be changed via
 * dedicated controls on both kinds.
 */

import { el, clear, formatDate, formatCZK, toast } from '../utils.js';
import { renderBackLink }         from '../lib/back-link.js';
import { copyable }               from '../lib/copyable.js';
import { renderLoadError }        from '../lib/load-error.js';
import { promptText }             from '../lib/text-modal.js';
import { promptRegisterHandover } from '../lib/register-prompt.js';
import { pageToken, isStale, remountPage } from '../lib/page-lifecycle.js';
import { CATEGORIES, DEVICE_STATUSES, contextTagInfo, flexiWebUrl } from '../config.js';
import {
  getDevice, updateDevice, getHandoversForDevice,
  getEmployee, getConfig, watchPendingChanges, cols,
} from '../db.js';
import { query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { calculateResaleValue, defaultFormulas, formulaKeyFor, pickResidual } from '../formula.js';
import { kvRow }         from '../lib/kv.js';
import { renderPageHead } from '../page-header.js';
import { openEmployeePicker } from '../employee-picker.js';
import { submitGearAction, cancelGearAction, removeGearDevice, setPersonalDevice, sendToLoot } from '../api.js';
import { openInTool } from '../hub.js';
import { reportLootResult, lootPath } from '../lib/loot.js';
import { gearActionLabel } from '../lib/gear-actions.js';
import { isTestMode } from '../test-mode.js';
import { navigate } from '../router.js';
import { confirmDialog } from '../standard/js/dialog.js';

export async function renderDeviceDetail(host, deviceId) {
  clear(host);
  host.appendChild(backLink());

  const skeleton = el('div', { class: 'skeleton', style: 'height:240px' });
  host.appendChild(skeleton);
  const token = pageToken(host);

  // Device first — the history lookup needs its SN (Abra-owned onboarding
  // handovers carry deviceId null and are matched by serial instead).
  let device;
  try {
    device = await getDevice(deviceId);
  } catch (err) {
    // No fallback on this one call, so an error used to leave the admin on a
    // permanent skeleton with only an unhandled rejection in the console.
    console.error('[device-detail] load failed', err);
    if (isStale(token)) return;
    skeleton.remove();
    host.appendChild(renderLoadError('Could not load this device.', () => renderDeviceDetail(host, deviceId)));
    return;
  }
  if (isStale(token)) return;
  if (!device) {
    skeleton.remove();
    host.appendChild(el('div', { class: 'empty' }, el('p', null, 'Device not found.')));
    return;
  }
  const [handovers, formulasDoc, pendingRows] = await Promise.all([
    getHandoversForDevice({ id: deviceId, sn: device.sn }).catch((err) => {
      console.error('[device-detail] history load failed', err);
      return [];
    }),
    getConfig('resaleFormulas').catch(() => null),
    loadPendingChangesForDevice(deviceId).catch(() => []),
  ]);
  skeleton.remove();
  const formulas = formulasDoc || defaultFormulas();

  const cat = CATEGORIES[device.category] || { label: device.category };
  const status = DEVICE_STATUSES[device.status] || { label: device.status, badge: 'idle' };
  const isFlexi = device.source === 'flexi';
  const editable = !isFlexi;

  // Effective holder = pending overlay on top of Abra. The Abra view in
  // `device.currentHolder` is what /devices says (i.e. what the last sync
  // brought in); the most-recent unresolved pending change can override
  // it — e.g. after `Transfer A→B` was submitted but Abra hasn't caught
  // up yet, the effective holder is B. Action prompts and the Assignment
  // card use the effective state so admins see / act on the intent they
  // already expressed, not the stale Abra view.
  const effective = await computeEffectiveHolder(device, pendingRows);

  const SOURCE_LABELS = {
    flexi: 'Flexi record', meraki: 'Meraki record', iru: 'IRU record',
    testing: 'Testing record', gear: 'Gear record',
  };
  host.appendChild(renderPageHead(
    device.name ? copyable(device.name, { label: 'name' }) : cat.label,
    el('p', { class: 'muted' },
      `${cat.label} · `,
      device.sn ? el('span', null, copyable(device.sn, { label: 'SN', mono: true, display: `SN ${device.sn}` }), ' · ') : 'No SN on record · ',
      SOURCE_LABELS[device.source] || 'Gear record',
    ),
  ));

  const grid = el('div', { class: 'detail-grid' });
  host.appendChild(grid);

  // Inverse of person-detail's layout: a device's identity/assignment/sync
  // details are the page, so they get the wide right column. The narrow
  // left rail holds the small action list (Transfer / Retire) plus the
  // Flexi sync metadata. Append `side` first → it lands in the 320px
  // column; `main` second → it gets the 1fr column.
  const side = el('aside', { class: 'detail-side' });
  const main = el('div', { class: 'stack' });
  grid.appendChild(side);
  grid.appendChild(main);

  // Pending-changes banner — above the two-column grid, so it's the first
  // thing after the title on every screen size (on a phone the Actions card
  // follows right after it). Swaps in/out via a live query against
  // /pending_changes for this deviceId; empty when nothing is pending or
  // recently reconciled.
  const pendingBanner = el('div');
  host.insertBefore(pendingBanner, grid);
  const unsubPending = watchPendingChanges({ deviceId }, (rows) => {
    renderPendingBanner(pendingBanner, rows, () => remountPage(host, renderDeviceDetail, deviceId));
  });
  host.addEventListener('cleanup', unsubPending, { once: true });

  // Decommissioned banner — this device left Abra's active pool ("Úplné
  // vyřazení"). Registry-only from here: no actions, no holder, and the one
  // thing that may still need doing is an MDM unenrollment.
  if (device.status === 'decommissioned') {
    main.appendChild(el('section', { class: `card${device.stillInMdm ? ' card-warn' : ''}` },
      el('div', { class: 'section-head' },
        'Decommissioned',
        el('span', { class: 'chip chip-error' }, 'Registry only'),
      ),
      el('p', { class: 'muted', style: 'margin:0' },
        `Fully disposed in Abra${device.disposedAt ? ` on ${formatDate(device.disposedAt)}` : ''} (Úplné vyřazení). `,
        device.lastHolderEmail ? `Last holder: ${device.lastHolderEmail}. ` : '',
        'This record is kept for reference — it is out of the active pool, invisible to employees, and has no actions.'),
      device.stillInMdm
        ? el('p', { class: 'meta-warning', style: 'margin:12px 0 0' },
            `Still enrolled in ${device.mdmSource === 'iru' ? 'IRU' : 'Meraki'} MDM — the device left the fleet but the MDM still lists it. Unenroll it there.`)
        : null,
    ));
  }

  main.appendChild(el('section', { class: 'card' },
    el('div', { class: 'section-head' }, 'Identity'),
    el('table', { class: 'kv' },
      kvRow('Category',     cat.label,                              !editable),
      kvRow('Name',         device.name ? copyable(device.name, { label: 'name' }) : '-', !editable),
      kvRow('Serial',       device.sn ? copyable(device.sn, { label: 'SN', mono: true }) : '-', !editable, true),
      ...(isFlexi ? [kvRow('Flexi code', device.flexiKod ? copyable(device.flexiKod, { label: 'Flexi code', mono: true }) : '-', true)] : []),
    ),
  ));

  main.appendChild(el('section', { class: 'card' },
    el('div', { class: 'section-head' }, 'Assignment'),
    el('table', { class: 'kv' },
      kvRow(device.personal ? 'Owner' : 'Holder',
        device.holderUnresolved
          ? el('span', { class: 'chip chip-error' }, `Orphan — tag "${device.holderEmployeeNumberRaw || '?'}" matches no employee`)
          : renderEffectiveHolderCell(device, effective),
        true),
      kvRow('Context tags',
        device.contextTags?.length
          ? el('div', { class: 'chips' },
              ...device.contextTags.map((t) => {
                const info = contextTagInfo(t);
                return el('span', { class: 'chip' }, info.label);
              }))
          : '-',
        !editable),
      kvRow('Status', el('span', { class: `chip chip-${status.badge}` }, status.label), false),
    ),
  ));

  // Reconcile card — reads device.match (computed by the materialise step)
  // and renders side-by-side Abra and Meraki views so admins can see exactly
  // why a flag is firing. Skipped entirely when the device has no Meraki
  // counterpart and isn't a Meraki-only orphan (i.e. Abra-only peripherals).
  const ms = device.match?.status || null;
  if (device.match?.inMeraki || device.match?.inIru || ms === 'orphan_no_abra') {
    const isOrphan      = ms === 'orphan_no_abra';
    const isMismatch    = ms === 'holder_mismatch';
    const isFromMdm     = ms === 'abra_unassigned';
    // MDM side — Meraki or IRU (both run during the IRU rollout).
    const mdm      = device.meraki || device.iru || null;
    const mdmLabel = device.meraki ? 'Meraki' : (device.iru ? 'IRU' : 'MDM');
    const headerChip = isOrphan
      ? el('span', { class: 'chip chip-error' }, 'Not in Abra')
      : isMismatch
        ? el('span', { class: 'chip chip-warning' }, 'Holder mismatch')
        : isFromMdm
          ? el('span', { class: 'chip' }, `From ${mdmLabel}`)
          : ms === 'personal'
            ? el('span', { class: 'chip' }, 'Personal')
            : el('span', { class: 'chip' }, `Abra · ${mdmLabel}`);

    const abra = device.abra || null;

    main.appendChild(el('section', { class: `card${isOrphan ? ' card-alert' : ''}${isMismatch ? ' card-warn' : ''}` },
      el('div', { class: 'section-head' }, 'Reconcile', headerChip),
      renderReconcileTable(abra, mdm, mdmLabel),
      isMismatch
        ? el('p', { class: 'muted', style: 'margin:12px 0 0' },
            `${mdmLabel} and Abra disagree on who holds this. Usually ${mdmLabel} is right (it reflects who physically has the device). Please reconcile by updating Abra.`)
        : null,
      isOrphan
        ? el('p', { class: 'muted', style: 'margin:12px 0 0' },
            `This device is in ${mdmLabel} MDM but not in Abra. Add it to Abra so the device pool is complete; the synthetic ${mdmLabel} record disappears as soon as Abra picks it up.`)
        : null,
      isFromMdm
        ? el('p', { class: 'muted', style: 'margin:12px 0 0' },
            `Abra doesn't list a holder for this device (the stitky tag is missing in Flexi). Gear is using ${mdmLabel}'s owner so the employee still sees it on /me. Adding the assignment in Abra would make this row match cleanly.`)
        : null,
    ));
  }

  // Testing-sheet reconcile card. Same pattern as Meraki — visible when
  // the device is in the QA testing sheet AND/OR when it's a testing
  // orphan (not in Abra at all). The user-facing intent: "yell a lot" if
  // not in Abra, "yell a little" if in Abra but the sheet's USED BY
  // doesn't match Abra's holder.
  const isTestingOrphan = ms === 'testing_not_in_abra';
  const testingFlag     = device.match?.testing?.status || null;
  if (device.match?.inTesting || isTestingOrphan) {
    const testing = device.testing || {};
    const isTestingMismatch = testingFlag === 'wrong_holder';
    const testingChip = isTestingOrphan
      ? el('span', { class: 'chip chip-error' }, 'Not in Abra (testing)')
      : isTestingMismatch
        ? el('span', { class: 'chip chip-warning' }, 'Testing mismatch')
        : el('span', { class: 'chip' }, 'Abra · Testing');

    const sheetUsedByCell = testing.usedByEmployeeId
      ? el('a', { href: `#/people/${testing.usedByEmployeeId}` }, testing.usedByName || testing.usedByEmail || testing.usedByEmployeeId)
      : (testing.usedByName
          ? el('span', null, testing.usedByName)
          : el('span', { class: 'muted' }, '(none)'));

    main.appendChild(el('section', {
      class: `card${isTestingOrphan ? ' card-alert' : ''}${isTestingMismatch ? ' card-warn' : ''}`,
    },
      el('div', { class: 'section-head' }, 'Testing sheet', testingChip),
      el('table', { class: 'kv' },
        kvRow('Sheet tab',     testing.sheetTab || '-', true),
        kvRow('Device tag',    testing.deviceTag
          ? el('code', null, testing.deviceTag)
          : el('span', { class: 'muted' }, '(none)'), true),
        kvRow('Model',         testing.model || '-', true),
        kvRow('Used by',       sheetUsedByCell, true),
        kvRow('Date borrowed', testing.dateBorrowed || '-', true),
        kvRow('Due back',      testing.dueBack || '-', true),
        kvRow('SN',            testing.sn ? el('span', { class: 'sn', style: 'font-family:var(--font-serial);font-size:var(--fs-small)' }, testing.sn) : '-', true),
        kvRow('IMEI',          testing.imei ? el('span', { class: 'sn', style: 'font-family:var(--font-serial);font-size:var(--fs-small)' }, testing.imei) : '-', true),
        kvRow('Last nudge',    testing.lastNudge || '-', true),
      ),
      isTestingOrphan
        ? el('p', { class: 'muted', style: 'margin:12px 0 0' },
            "This device is on the QA testing sheet but Abra doesn't have it (no SN match, no IMEI match). Add it to Abra so it's tracked in inventory; once Abra has it, this orphan card will disappear on the next sync.")
        : isTestingMismatch
          ? el('p', { class: 'muted', style: 'margin:12px 0 0' },
              `The testing sheet says ${device.match?.testing?.sheetUsedByName || '(?)'} has this device, but Abra has it on ${device.match?.testing?.abraHolderName || (device.abra?.holderEmail || '(?)')}. Usually the testing sheet is right — fix the Abra assignment to match.`)
          : null,
    ));
  }

  if (isFlexi) {
    main.appendChild(el('section', { class: 'card' },
      el('div', { class: 'section-head' }, 'Flexi details (read-only)'),
      el('table', { class: 'kv' },
        kvRow('Purchase date',  formatDate(device.purchaseDate), true),
        kvRow('Purchase price', device.purchasePrice != null ? formatCZK(device.purchasePrice) : '-', true),
        kvRow('Cost center',    device.costCenter || '-',        true),
      ),
    ));
  }

  // Calculated resale value — visible on any device with a price + formula
  // applicable to its category (laptops, phones today). Useful for ad-hoc
  // sales not tied to an offboarding workflow ("user wants to buy his
  // current laptop from STRV, what does it cost today?").
  const resale = calculateResaleValue(device, formulas);
  if (resale != null) {
    const formula = formulas[formulaKeyFor(device.category)];
    const effectiveFloor = pickResidual(formula, device.name);

    // Forecast: 3 future months. Useful when planning an offboarding
    // some weeks out — admin can see what the device will cost on the
    // 1st of each upcoming month. Depreciation steps on the 1st (see
    // utils.monthsBetween), so each row is the price for that entire
    // calendar month.
    const forecast = [];
    const today = new Date();
    for (let i = 1; i <= 3; i += 1) {
      const d = new Date(today.getFullYear(), today.getMonth() + i, 1);
      forecast.push({ date: d, value: calculateResaleValue(device, formulas, d) });
    }
    const monthLabel = (d) => d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });

    main.appendChild(el('section', { class: 'card' },
      el('div', { class: 'section-head' }, "Today's resale value"),
      el('p', { style: 'font-size:32px;font-weight:700;margin:8px 0 4px' }, formatCZK(resale)),
      el('p', { class: 'muted', style: 'font-size:var(--fs-small);margin:0 0 16px' },
        device.purchasePrice
          ? `From ${formatCZK(device.purchasePrice)} purchase, depreciated over ${formula?.depreciationMonths || '?'} months. Floor ${formatCZK(effectiveFloor)}. Coefficients editable in Settings.`
          : 'No purchase price on the Flexi record; formula floor used.',
      ),
      el('table', { class: 'kv' },
        ...forecast.map((row) =>
          el('tr', null,
            el('td', { class: 'k' }, monthLabel(row.date)),
            el('td', { class: 'v' }, row.value != null ? formatCZK(row.value) : '—'),
          ),
        ),
      ),
    ));
  }

  // gearNotes is admin-owned and survives the sync; `notes` mirrors Abra's
  // poznam and is rebuilt every run (the old target for this textarea, which
  // is why "Saved in Gear" notes kept disappearing). Fall back to `notes` so
  // gear-source handover notes and pre-existing text still render.
  const notesArea = el('textarea', { rows: 3, value: device.gearNotes ?? device.notes ?? '' });
  const savedFlash = el('span', { class: 'muted', style: 'font-size:var(--fs-small);margin-left:8px;opacity:0;transition:opacity 0.2s' }, 'Saved');
  notesArea.addEventListener('blur', async () => {
    try {
      await updateDevice(device.id, { gearNotes: notesArea.value });
      savedFlash.style.opacity = '1';
      setTimeout(() => { savedFlash.style.opacity = '0'; }, 1200);
    } catch (err) {
      toast(err.message || 'Could not save notes.', 'error');
    }
  });
  main.appendChild(el('section', { class: 'card' },
    el('div', { class: 'section-head' }, 'Gear notes', savedFlash),
    notesArea,
    el('p', { class: 'muted', style: 'font-size:var(--fs-small);margin-top:6px' },
      isFlexi ? 'Saved in Gear only - Abra is not modified.' : 'Saved in Gear.'),
  ));

  main.appendChild(el('section', { class: 'card' },
    el('div', { class: 'section-head' }, 'History', el('span', { class: 'count' }, `(${handovers.length})`)),
    handovers.length
      ? el('div', { class: 'stack', style: 'gap:8px' },
          ...handovers.map(renderHandoverRow))
      : el('p', { class: 'muted' }, 'No handovers logged yet.'),
  ));

  // Actions rail — Gear-side action menu. Every click writes a row into
  // /pending_changes, pings Slack so Finance can do the Abra side, and
  // sits in the queue until the next sync confirms Abra reflects the
  // change. The Pending banner above shows in-flight state.
  //
  // We show the full menu on any Flexi-rooted device. Meraki-only or
  // testing-only orphans need to be in Abra first — the Reconcile tab
  // exists for that and we steer admins there. Gear-source peripherals
  // (keyboards, mice, etc.) keep the old simple Retire flow — they're
  // outside Abra's scope by design.
  side.appendChild(renderActionsCard(device, deviceId, effective, formulas, host));

  if (isFlexi && device.flexiKod) {
    // Per-record "last sync" timestamp lived on the old shared-write
    // /devices doc; after the materialise refactor each device is rebuilt
    // from /flexi_devices nightly, so the per-record timestamp would
    // always be "today" and isn't useful. Cross-source freshness lives in
    // Settings (Last sync: shows the cron's last-success time).
    side.appendChild(el('div', { class: 'card' },
      el('div', { class: 'section-head' }, 'Flexi'),
      el('p', null, 'Flexi code: ', el('span', { class: 'sn', style: 'font-family:var(--font-serial);font-size:var(--fs-small)' }, device.flexiKod)),
      el('a', {
        href: flexiWebUrl(device.flexiKod),
        target: '_blank', rel: 'noopener noreferrer',
        class: 'btn btn-sm',
        style: 'margin-top:8px',
      }, 'Open in Flexi'),
    ));
  }
}

function backLink() {
  // Context-aware: goes back to wherever the device was opened from (a
  // person page, Reconcile, the Devices list, …); '#/devices' on deep link.
  return renderBackLink({ fallbackHref: '#/devices', fallbackLabel: 'Back to devices' });
}

/**
 * Render the Abra ⨝ Meraki reconcile table. A single 4-column table so the
 * shared rows (Holder, SN) line up across the two sources — that alignment
 * is the whole point of this card, since the admin's eye reads the row
 * left-to-right looking for mismatches.
 *
 * Row order:
 *   1. Holder (Abra "Resolved to" | Meraki "Owner")
 *   2. SN
 *   3+ source-specific rows zipped (Abra: stitky, poznam, holder source,
 *      flexi code; Meraki: model, OS, last seen) — these are different
 *      concepts so the row pairing doesn't carry meaning, but keeping them
 *      in one table preserves the row borders and column widths.
 *
 * Orphan case (no Abra side) falls back to a single Meraki-only kv table
 * — no alignment needed if there's nothing to align against.
 */
function renderReconcileTable(abra, meraki, mdmLabel = 'Meraki') {
  if (!abra && meraki) {
    return el('table', { class: 'kv', style: 'margin-top:8px' },
      el('thead', null, el('tr', null,
        el('th', { colspan: 2, style: 'text-align:left;padding-bottom:8px' },
          el('span', { class: 'label' }, `${mdmLabel} (no Abra record)`)),
      )),
      kvRow('Owner',
        meraki.ownerEmployeeId
          ? el('a', { href: `#/people/${meraki.ownerEmployeeId}` }, meraki.ownerEmail || meraki.ownerEmployeeId)
          : (meraki.ownerEmail ? copyable(meraki.ownerEmail, { label: 'email' }) : el('span', { class: 'muted' }, '(unknown)')),
        true),
      kvRow('SN',        meraki.sn ? copyable(meraki.sn, { label: 'SN', mono: true }) : '-', true, true),
      kvRow('Model',     meraki.systemModel || '-', true),
      // The MDM hostname ("Martin's MacBook Pro"). The page title shows the
      // MODEL for not-in-Abra devices, so surface the hostname here.
      kvRow('Device name', meraki.name ? copyable(meraki.name, { label: 'device name' }) : '-', true),
      kvRow('OS',        meraki.osName || '-', true),
      kvRow('First enrolled', formatDate(meraki.firstEnrolledAt), true),
      kvRow('Last seen', formatDate(meraki.lastSeenAt), true),
    );
  }

  // Shared rows — these are the alignment-critical pairs.
  const rows = [
    {
      abraK: 'Resolved to',
      abraV: abra?.holderEmployeeId
        ? el('a', { href: `#/people/${abra.holderEmployeeId}` }, abra.holderEmail || abra.holderEmployeeId)
        : el('span', { class: 'muted' }, abra?.holderUnresolved ? 'Not found in Bamboo' : '—'),
      merakiK: 'Owner',
      merakiV: meraki?.ownerEmployeeId
        ? el('a', { href: `#/people/${meraki.ownerEmployeeId}` }, meraki.ownerEmail || meraki.ownerEmployeeId)
        : (meraki?.ownerEmail
            ? el('span', null, meraki.ownerEmail)
            : el('span', { class: 'muted' }, '(unknown)')),
    },
    {
      abraK: 'SN',
      abraV: abra?.sn
        ? el('span', { class: 'sn', style: 'font-family:var(--font-serial);font-size:var(--fs-small)' }, abra.sn)
        : el('span', { class: 'muted' }, '—'),
      merakiK: 'SN',
      merakiV: meraki?.sn
        ? el('span', { class: 'sn', style: 'font-family:var(--font-serial);font-size:var(--fs-small)' }, meraki.sn)
        : el('span', { class: 'muted' }, '—'),
    },
  ];

  // Source-specific rows zipped by index. They're different concepts on
  // each side, but stacking them in the same table keeps the borders /
  // spacing uniform with the shared rows above.
  const abraOnly = abra ? [
    {
      k: 'Holder source',
      v: abra.holderSource
        ? el('span', null, `From ${abra.holderSource} · employee ${abra.holderEmployeeNumber || '?'}`)
        : el('span', { class: 'muted' }, 'No assignment'),
    },
    {
      k: 'Stitky',
      v: abra.rawStitky
        ? el('span', { class: 'sn', style: 'font-family:var(--font-serial);font-size:var(--fs-small)' }, abra.rawStitky)
        : el('span', { class: 'muted' }, '(empty)'),
    },
    {
      k: 'Poznam',
      v: abra.rawPoznam
        ? el('span', { style: 'font-family:var(--font-serial);font-size:var(--fs-small)' }, abra.rawPoznam)
        : el('span', { class: 'muted' }, '(empty)'),
    },
    { k: 'Flexi code', v: abra.flexiKod || '-' },
  ] : [];

  const merakiOnly = meraki ? [
    { k: 'Model',     v: meraki.systemModel || '-' },
    { k: 'OS',        v: meraki.osName || '-' },
    { k: 'First enrolled', v: formatDate(meraki.firstEnrolledAt) },
    { k: 'Last seen', v: formatDate(meraki.lastSeenAt) },
  ] : [];

  const extraRows = Math.max(abraOnly.length, merakiOnly.length);
  for (let i = 0; i < extraRows; i += 1) {
    rows.push({
      abraK:   abraOnly[i]?.k   || null,
      abraV:   abraOnly[i]?.v   || null,
      merakiK: merakiOnly[i]?.k || null,
      merakiV: merakiOnly[i]?.v || null,
    });
  }

  // .kv defaults `.k` to 38% width, which would push the two halves to
  // 76% combined. Override per-cell here so each label/value pair takes
  // a quarter of the row. Two thin label columns + two value columns.
  return el('table', { class: 'kv kv-pair', style: 'margin-top:8px;table-layout:fixed' },
    el('thead', null, el('tr', null,
      el('th', { colspan: 2, style: 'text-align:left;padding-bottom:8px' }, el('span', { class: 'label' }, 'Abra')),
      el('th', { colspan: 2, style: 'text-align:left;padding-bottom:8px;padding-left:16px' }, el('span', { class: 'label' }, mdmLabel)),
    )),
    el('tbody', null,
      ...rows.map((r) => el('tr', null,
        el('td', { class: 'k', style: 'width:14%' }, r.abraK || ''),
        el('td', { class: 'v', style: 'width:36%' }, r.abraV || el('span', { class: 'muted' }, '')),
        el('td', { class: 'k', style: 'width:14%;padding-left:16px' }, r.merakiK || ''),
        el('td', { class: 'v', style: 'width:36%' }, r.merakiV || el('span', { class: 'muted' }, '')),
      )),
    ),
  );
}

function renderHandoverRow(h) {
  const when = formatDate(h.ts);
  const dir = h.direction === 'in' ? 'retrieved' : (h.direction === 'transfer' ? 'transferred' : 'handed over');
  return el('div', { class: 'row', style: 'border-bottom:1px solid var(--color-surface-pressed);padding:8px 0;font-size:var(--fs-small)' },
    el('span', { style: 'width:110px;color:var(--color-text-tertiary)' }, when),
    el('span', { style: 'flex:1' }, `${dir}${h.employeeName ? `: ${h.employeeName}` : ''}${h.outcome ? ` (${h.outcome})` : ''}${h.note ? ` · ${h.note}` : ''}`),
    el('span', { class: 'muted' }, h.actor || ''),
  );
}

// ── Gear actions card ─────────────────────────────────────────────────────
//
// Six actions, each writes a /pending_changes row + posts to Slack. The
// Gear-source path keeps the legacy Retire button (no Abra to reconcile
// against). Meraki / Testing orphans are read-only here — admins are
// pointed at Reconcile to add them to Abra first.
function renderActionsCard(device, deviceId, effective, formulas, host) {
  // Decommissioned = registry-only. Every action (transfer, sell, dispose…)
  // presumes an active Abra asset; this one is already written off.
  if (device.status === 'decommissioned') {
    return el('div', { class: 'card' },
      el('div', { class: 'section-head' }, 'Actions'),
      el('p', { class: 'muted', style: 'margin:0' },
        'None — this device is decommissioned in Abra. The record stays for reference only.'),
    );
  }

  // On Loot: the listing is Loot's now (price, condition, buyer) — no Gear
  // action until Loot hands it over or reverses.
  if (device.gearLoot?.listingId) return renderLootCard(device);

  const isGear    = device.source === 'gear';
  const isOrphan  = device.source === 'meraki' || device.source === 'iru' || device.source === 'testing';
  // The effective view drives every action: hand-over becomes Transfer if
  // there's already an effective holder (pending or Abra), Take back
  // names the effective holder, etc. /devices.currentHolder stays Abra-
  // canonical for sync purposes.
  const hasHolder = !!effective.employeeId;

  if (isOrphan) {
    // An MDM / testing-sheet device Abra doesn't know — typically a new
    // laptop that enrolled in IRU before Finance registered it. The ONE
    // action that makes sense here is handing it to someone, and that is
    // exactly a `register`: Finance gets the usual hand-over ping, creates
    // the asset and assigns it; the pending row reconciles by serial once
    // the Flexi record lands (this orphan doc is swept, the Flexi doc takes
    // over). Sell / dispose / … presume an Abra asset → still via Reconcile.
    // A personal laptop (the person's own, enrolled for data security): the
    // only action is undoing the mark.
    if (device.personal) {
      return el('div', { class: 'card' },
        el('div', { class: 'section-head' }, 'Actions'),
        el('p', { class: 'muted', style: 'margin:0 0 12px' }, 'Personal device.'),
        el('button', {
          class: 'btn',
          onClick: () => togglePersonal(device, deviceId, false, host),
        }, 'Mark as company device'),
      );
    }
    const pendingHere = effective.source === 'pending';
    const canRegister = !!device.sn && !pendingHere;
    const canPersonal = !!device.sn && !pendingHere && device.source !== 'testing';
    return el('div', { class: 'card' },
      el('div', { class: 'section-head' }, 'Actions'),
      canRegister || canPersonal
        ? el('div', { class: 'stack' },
            canRegister
              ? el('button', { class: 'btn btn-primary', onClick: () => promptRegisterHandover({ ...device, id: deviceId }) }, 'Hand over to…')
              : null,
            canPersonal
              ? el('button', { class: 'btn', onClick: () => togglePersonal(device, deviceId, true, host) }, 'Mark as personal')
              : null)
        : null,
      el('p', { class: 'muted', style: `margin:${canRegister ? '12px' : '0'} 0 0;font-size:var(--fs-small)` },
        pendingHere ? 'Not in Abra yet · hand-over pending. ' : (device.sn ? 'Not in Abra yet. ' : 'Not in Abra yet, no serial. '),
        'Other actions: add it via ', el('a', { href: '#/reconcile' }, 'Reconcile'), ' first.'),
    );
  }

  if (isGear) {
    return el('div', { class: 'card' },
      el('div', { class: 'section-head' }, 'Actions'),
      el('div', { class: 'stack' },
        el('button', {
          class: 'btn',
          onClick: async () => {
            if (!await confirmDialog({
              title: 'Remove this peripheral?',
              body: 'Deletes the record. The handover stays in the audit log.',
              confirm: 'Remove',
              danger: true,
            })) return;
            try {
              await removeGearDevice({ deviceId: device.id });
              toast('Peripheral removed.', 'success');
              navigate('/devices'); // the record is gone — nothing left to show here
            } catch (err) {
              toast(err.message || 'Remove failed.', 'error');
            }
          },
        }, 'Remove'),
      ),
      el('p', { class: 'muted', style: 'margin:12px 0 0;font-size:var(--fs-small)' },
        "Gear-only peripheral — not tracked in Abra. Removing deletes the record "
        + '(the handover history is preserved in the audit log).'),
    );
  }

  // Flexi-rooted device → the full pending-action menu.
  return el('div', { class: 'card' },
    el('div', { class: 'section-head' }, 'Actions'),
    el('p', { class: 'muted', style: 'margin:0 0 12px;font-size:var(--fs-small)' },
      'Submitting an action queues a Slack message to Finance and waits for Abra to catch up. The status reconciles automatically on the next sync.'),
    el('div', { class: 'stack' },
      el('button', {
        class: 'btn btn-primary',
        onClick: () => promptHandover(device, deviceId, effective, host),
      }, hasHolder ? 'Transfer to…' : 'Hand over to…'),
      hasHolder
        ? el('button', {
            class: 'btn',
            onClick: () => promptTakeback(device, deviceId, effective, host),
          }, 'Take back')
        : null,
      el('button', {
        class: 'btn',
        onClick: () => promptMakeTesting(device, deviceId, host),
      }, 'Make into test device'),
      el('button', {
        class: 'btn',
        onClick: () => promptSell(device, deviceId, effective, formulas, host),
      }, 'Sell…'),
      device.personal
        ? null
        : el('button', {
            class: 'btn',
            onClick: () => promptSendToLoot(device, deviceId, host),
          }, 'Send to Loot'),
      el('button', {
        class: 'btn',
        onClick: () => promptDispose(device, deviceId, host),
      }, 'Dispose'),
    ),
  );
}

// ── Loot ──────────────────────────────────────────────────────────────────
function renderLootCard(device) {
  const loot = device.gearLoot;
  const sold = loot.status === 'sold';
  const buyer = loot.buyer?.name || loot.buyer?.email;
  return el('div', { class: 'card' },
    el('div', { class: 'section-head' }, 'Loot'),
    el('div', { class: 'row', style: 'gap:8px' },
      el('span', { class: `chip ${sold ? 'chip-success' : 'chip-ready'}` }, sold ? 'Sold' : 'For sale'),
      el('span', { class: 'serial muted', style: 'font-size:var(--fs-small)' }, loot.listingId)),
    sold
      ? el('p', { style: 'margin:12px 0 0' }, [buyer, loot.priceCzk != null ? formatCZK(loot.priceCzk) : null].filter(Boolean).join(' · ') || 'Sold')
      : el('p', { class: 'muted', style: 'margin:12px 0 0;font-size:var(--fs-small)' }, 'Price and condition are set in Loot.'),
    loot.url
      ? el('button', { class: 'btn btn-sm', style: 'margin-top:12px', onClick: () => openInTool('loot', lootPath(loot.url), loot.url) }, 'Open in Loot')
      : null,
  );
}

async function promptSendToLoot(device, deviceId, host) {
  if (!await confirmDialog({
    title: `Send ${device.name || 'this device'} to Loot?`,
    body: 'Price and condition are set in Loot.',
    confirm: 'Send to Loot',
  })) return;
  try {
    const res = await sendToLoot({ deviceId });
    reportLootResult(res);
    remountPage(host, renderDeviceDetail, deviceId);
  } catch (err) {
    console.error('[device-detail] send to loot failed', err);
    toast(err.message || 'Send to Loot failed.', 'error', 8000);
  }
}

// ── Pending-changes banner ────────────────────────────────────────────────
function renderPendingBanner(host, rows, refresh) {
  clear(host);
  if (!rows.length) return;
  // Show pending first, then recently-reconciled (last 24h) as a cool-down
  // confirmation. Cancelled rows are hidden.
  const now = Date.now();
  const visible = rows.filter((r) => {
    if (r.status === 'pending') return true;
    if (r.status === 'reconciled' && r.reconciledAt?.toMillis
        && now - r.reconciledAt.toMillis() < 24 * 60 * 60 * 1000) return true;
    return false;
  });
  if (!visible.length) return;

  for (const r of visible) {
    const isPending = r.status === 'pending';
    const card = el('section', {
      class: `card ${isPending ? 'card-warn' : ''}`,
      style: 'margin-bottom:12px',
    },
      el('div', { class: 'row', style: 'justify-content:space-between;align-items:flex-start;gap:12px;flex-wrap:wrap' },
        el('div', null,
          el('div', { style: 'font-weight:600' },
            isPending
              ? 'Pending in Abra'
              : (r.reconciledVia === 'abra_removed'
                  ? 'Resolved — device removed from Abra'
                  : r.reconciledVia === 'superseded'
                    ? 'Superseded by a newer action'
                    : 'Reconciled in Abra'),
            el('span', { class: `chip ${isPending ? 'chip-warning' : 'chip-success'}`, style: 'margin-left:8px' },
              gearActionLabel(r.action)),
          ),
          el('div', { class: 'muted', style: 'font-size:var(--fs-small);margin-top:4px' },
            describePending(r)),
          r.slack && !r.slack.ok && r.slack.attempted
            ? el('div', { class: 'muted', style: 'font-size:var(--fs-small);margin-top:4px;color:var(--color-error)' },
                `Slack delivery failed: ${r.slack.error || r.slack.reason || 'unknown error'}`)
            : null,
        ),
        isPending
          ? el('button', {
              class: 'btn btn-ghost btn-sm',
              onClick: async () => {
                const reason = await promptText({
                  title: 'Cancel pending change', label: 'Note for Finance', placeholder: 'Optional',
                  confirmLabel: 'Cancel change', cancelLabel: 'Keep', multiline: true,
                });
                if (reason === null) return; // dialog dismissed
                try {
                  const res = await cancelGearAction({ pendingId: r.id, reason: reason || null });
                  if (res?.slack?.ok) toast('Cancelled. Slack notified.', 'success');
                  else toast(`Cancelled, BUT Slack delivery failed — ${res?.slack?.error || res?.slack?.reason || 'unknown'}. Will retry every 10 min.`, 'error', 8000);
                  refresh();
                } catch (err) {
                  toast(err.message || 'Cancel failed.', 'error');
                }
              },
            }, 'Cancel')
          : null,
      ),
    );
    host.appendChild(card);
  }
}

function describePending(r) {
  const bits = [];
  if (r.toHolder?.name)   bits.push(`→ ${r.toHolder.name}`);
  if (r.fromHolder?.name) bits.push(`← ${r.fromHolder.name}`);
  if (r.saleData) {
    const buyer = r.saleData.buyer?.name || r.saleData.buyer?.email || 'buyer';
    const price = r.saleData.salePriceCzk != null ? `${r.saleData.salePriceCzk} CZK` : '(price unset)';
    bits.push(`${buyer} · ${price}`);
  }
  if (r.note) bits.push(`note: "${r.note}"`);
  const when = r.ts?.toDate?.() ? formatDate(r.ts.toDate()) : '';
  const who  = r.actor?.email || r.actor?.name || '';
  return [bits.join(' · '), `submitted ${when}${who ? ` by ${who}` : ''}`].filter(Boolean).join(' · ');
}

// ── Action prompts ────────────────────────────────────────────────────────

function promptHandover(device, deviceId, effective, host) {
  const isTransfer = !!effective.employeeId;
  openEmployeePicker({
    title:        isTransfer ? `Transfer ${device.name || 'device'}` : `Hand over ${device.name || 'device'}`,
    subtitle:     isTransfer
      ? `Currently with ${effective.name || 'someone'}. Pick the new holder.`
      : 'Pick the employee receiving this device.',
    confirmLabel: isTransfer ? 'Transfer' : 'Hand over',
    onPick: async (employee) => {
      const action = isTransfer ? 'transfer' : 'handover';
      const payload = {
        toHolder: { employeeId: employee.id, name: employee.fullName || null, email: employee.email || null },
        fromHolder: isTransfer
          ? { employeeId: effective.employeeId, name: effective.name || null }
          : null,
      };
      try {
        const res = await submitGearAction({ action, deviceId, payload });
        reportSubmitResult(res, isTransfer ? 'Transfer' : 'Handover');
      } catch (err) {
        console.error('[device-detail] submit failed', err);
        toast(err.message || 'Submit failed.', 'error');
      }
    },
  });
  void host;
}

async function togglePersonal(device, deviceId, personal, host) {
  const name = device.name || 'this device';
  if (!await confirmDialog({ title: personal ? `Mark ${name} as personal?` : `Mark ${name} as a company device?`, confirm: personal ? 'Mark personal' : 'Mark company' })) return;
  try {
    await setPersonalDevice({ deviceId, personal });
    toast(personal ? 'Marked personal.' : 'Marked company device.', 'success');
    remountPage(host, renderDeviceDetail, deviceId);
  } catch (err) {
    console.error('[device-detail] personal toggle failed', err);
    toast(err.message || 'Could not update the device.', 'error');
  }
}

async function promptTakeback(device, deviceId, effective) {
  if (!await confirmDialog({
    title: `Take ${device.name || 'this device'} back from ${effective.name || 'the current holder'}?`,
    body: 'It goes into storage until reassigned.',
    confirm: 'Take back',
  })) return;
  submitGearAction({
    action: 'takeback',
    deviceId,
    payload: {
      fromHolder: effective.employeeId
        ? { employeeId: effective.employeeId, name: effective.name || null }
        : null,
    },
  }).then((res) => reportSubmitResult(res, 'Takeback'))
    .catch((err) => toast(err.message || 'Submit failed.', 'error'));
}

async function promptMakeTesting(device, deviceId) {
  if (!await confirmDialog({
    title: `Move ${device.name || 'this device'} into the testing pool?`,
    body: 'Finance adds the TESTING tag in Abra.',
    confirm: 'Make test device',
  })) return;
  submitGearAction({ action: 'make_testing', deviceId, payload: {} })
    .then((res) => reportSubmitResult(res, 'Testing-pool submission'))
    .catch((err) => toast(err.message || 'Submit failed.', 'error'));
}

async function promptDispose(device, deviceId) {
  if (!await confirmDialog({
    title: `Dispose of ${device.name || 'this device'}?`,
    body: 'It was thrown away. Finance marks it retired in Abra.',
    confirm: 'Dispose',
    danger: true,
  })) return;
  submitGearAction({ action: 'dispose', deviceId, payload: {} })
    .then((res) => reportSubmitResult(res, 'Disposal'))
    .catch((err) => toast(err.message || 'Submit failed.', 'error'));
}

/**
 * Toast helper that surfaces Slack delivery state to the admin who
 * triggered the action. Success = green; Slack queued for retry = red,
 * since the user explicitly asked to be told when Slack fails.
 */
function reportSubmitResult(res, label) {
  if (!res) return;
  if (res.slack?.ok) {
    toast(`${label} submitted. Slack notified.`, 'success');
    return;
  }
  // Submission landed in Firestore + Pending tab, but Slack delivery
  // didn't go through. Will retry every 10 minutes from now via the
  // scheduled cron + drain on next "Sync everything".
  const why = res.slack?.error || res.slack?.reason || 'unknown reason';
  toast(
    `${label} recorded, BUT Slack delivery failed — ${why}. Will retry automatically every 10 min. Fix the webhook on Settings if it's a config issue.`,
    'error',
    8000,
  );
}

function promptSell(device, deviceId, effective, formulas, host) {
  const holderName = effective.name;
  const suggested = calculateResaleValue(device, formulas) || 0;
  // A custom inline form rendered into employeePicker's extraSlot so the
  // admin can pick the buyer AND enter a price in one modal.
  const priceInput = el('input', {
    type: 'number',
    min: '0',
    step: '1',
    placeholder: 'CZK',
    style: 'width:140px',
    value: String(suggested),
  });
  const externalCheckbox = el('input', { type: 'checkbox' });
  const warnLine = el('p', { class: 'muted', style: 'font-size:var(--fs-small);margin:4px 0 0' }, '');
  function refreshWarn() {
    const v = Number(priceInput.value);
    if (!Number.isFinite(v) || v <= 0) {
      warnLine.textContent = 'Enter a price greater than 0.';
      warnLine.style.color = 'var(--color-error)';
      return;
    }
    const diff = v - suggested;
    if (Math.abs(diff) < 1) {
      warnLine.textContent = `Matches the formula suggestion (${formatCZK(suggested)}).`;
      warnLine.style.color = '';
    } else if (diff < 0) {
      warnLine.textContent = `Below the formula suggestion of ${formatCZK(suggested)} by ${formatCZK(Math.abs(diff))}. OK to proceed — admin discretion.`;
      warnLine.style.color = 'var(--color-error)';
    } else {
      warnLine.textContent = `Above the formula suggestion of ${formatCZK(suggested)} by ${formatCZK(diff)}.`;
      warnLine.style.color = '';
    }
  }
  priceInput.addEventListener('input', refreshWarn);
  refreshWarn();

  const extra = el('div', { class: 'stack', style: 'gap:8px;margin-top:12px' },
    el('div', { class: 'row', style: 'gap:8px;align-items:center' },
      el('label', { class: 'label' }, 'Sale price'), priceInput, el('span', { class: 'muted' }, 'CZK'),
    ),
    warnLine,
    el('label', { style: 'display:inline-flex;gap:6px;align-items:center;font-size:var(--fs-small)' },
      externalCheckbox, 'Mark buyer as external (not a STRV employee)'),
  );

  openEmployeePicker({
    title:        `Sell ${device.name || 'device'}`,
    subtitle:     holderName
      ? `Currently with ${holderName}. Pick the buyer (employee), or tick "external" if it's an outside party.`
      : 'Pick the buyer (employee), or tick "external" if it\'s an outside party.',
    confirmLabel: 'Submit sale',
    extraSlot:    extra,
    onPick: async (employee) => {
      const price = Number(priceInput.value);
      if (!Number.isFinite(price) || price <= 0) {
        toast('Enter a valid sale price.', 'error');
        throw new Error('Invalid sale price'); // keep modal open
      }
      const payload = {
        toHolder: { employeeId: employee.id, name: employee.fullName || null, email: employee.email || null },
        saleData: {
          buyer: { employeeId: employee.id, name: employee.fullName || null, email: employee.email || null },
          salePriceCzk:      price,
          suggestedPriceCzk: suggested || null,
          isExternal:        externalCheckbox.checked,
        },
      };
      try {
        const res = await submitGearAction({ action: 'sell', deviceId, payload });
        reportSubmitResult(res, `Sale at ${formatCZK(price)}`);
      } catch (err) {
        console.error('[device-detail] submit sale failed', err);
        toast(err.message || 'Submit failed.', 'error');
      }
    },
  });
  void host;
}

// ── Effective holder (Abra view + pending overlay) ───────────────────────
//
// Pull the most recent pending_changes for this device. If any
// unresolved row sets a new holder (handover / transfer), that's the
// effective holder. If the latest unresolved row removes the holder
// (takeback / sell / dispose / make_testing), the effective holder is
// none. If there's nothing pending, fall back to /devices.currentHolder.
//
// Why a separate read: /devices is the Abra mirror — we deliberately
// never write the pending change into it (sync would overwrite us next
// round). The action layer overlays pending state at read-time so
// admins act on the latest intent, not the stale Abra view.

async function loadPendingChangesForDevice(deviceId) {
  // In test mode the per-user sandbox collection has no composite index,
  // so drop the orderBy from the query and sort the (tiny) result set in
  // memory. Production keeps the indexed deviceId+ts query.
  const clauses = [where('deviceId', '==', deviceId)];
  if (!isTestMode()) clauses.push(orderBy('ts', 'desc'), limit(20));
  const snap = await getDocs(query(cols.pendingChanges(), ...clauses));
  const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  if (isTestMode()) {
    rows.sort((a, b) => (b.ts?.toMillis?.() || 0) - (a.ts?.toMillis?.() || 0));
    return rows.slice(0, 20);
  }
  return rows;
}

async function computeEffectiveHolder(device, pendingRows) {
  const latestPending = pendingRows.find((r) => r.status === 'pending');
  if (latestPending) {
    if (['register', 'handover', 'transfer'].includes(latestPending.action)) {
      return {
        employeeId: latestPending.toHolder?.employeeId || null,
        name:       latestPending.toHolder?.name || null,
        source:     'pending',
        pendingAction: latestPending.action,
      };
    }
    if (['takeback', 'sell', 'dispose', 'make_testing'].includes(latestPending.action)) {
      return { employeeId: null, name: null, source: 'pending', pendingAction: latestPending.action };
    }
  }
  if (device.currentHolder) {
    const e = await getEmployee(device.currentHolder).catch(() => null);
    return {
      employeeId: device.currentHolder,
      name:       e?.fullName || device.currentHolder,
      source:     'abra',
      pendingAction: null,
    };
  }
  return { employeeId: null, name: null, source: 'abra', pendingAction: null };
}

/**
 * Render the Holder cell in the Assignment card. Three shapes:
 *  - Effective holder is the same as Abra holder → plain name link.
 *  - Effective holder differs (pending overlay) → name link + a muted
 *    "pending: <action>" note. The pending banner above already shows
 *    the full details; this is just so the Assignment card itself
 *    doesn't lie about the live state.
 *  - No holder → em-dash.
 */
function renderEffectiveHolderCell(device, effective) {
  if (!effective.employeeId) {
    if (effective.source === 'pending') {
      return el('span', null,
        '—',
        el('span', { class: 'muted', style: 'margin-left:8px' },
          `(pending ${gearActionLabel(effective.pendingAction).toLowerCase()})`),
      );
    }
    return '-';
  }
  const link = el('a', { href: `#/people/${effective.employeeId}` },
    effective.name || effective.employeeId);
  if (effective.source === 'pending') {
    return el('span', null,
      link,
      el('span', { class: 'muted', style: 'margin-left:8px' },
        `(pending ${gearActionLabel(effective.pendingAction).toLowerCase()})`),
      device.currentHolder && device.currentHolder !== effective.employeeId
        ? el('div', { class: 'muted', style: 'font-size:var(--fs-small);margin-top:2px' },
            'Abra still shows the previous holder until the next sync confirms.')
        : null,
    );
  }
  return link;
}
